import config from '../../config.js';

import { dateFormat } from '../../util/date.js';

// Stable @id for the two site-wide entities. Every page-level @graph (category, product, article)
// references them by @id instead of redefining them, so search engines and LLMs consolidate the
// whole site onto a single brand entity instead of building one per page.
export const ORGANIZATION_ID = `${config.url}#organization`;
export const WEBSITE_ID = `${config.url}#website`;

// Every country the shop sells to. Entity-level claim only ("where we operate"), with no promise
// on price or delay attached, so it can stay broad.
export const AREA_SERVED_COUNTRIES = ['FR', 'BE', 'DE', 'NL', 'GB', 'CH', 'LU', 'DK', 'IT', 'ES', 'PT', 'NO', 'SE', 'FI', 'AT', 'PL', 'CZ', 'HU', 'SK', 'SI', 'EE', 'LV', 'LT', 'RO', 'BG', 'HR', 'CY', 'MT', 'IE'];

// Deliberately narrower than AREA_SERVED_COUNTRIES: consumed by the Offer shippingDetails in
// ProductSchema.js, which hardcodes a 0 EUR rate and a 2-5 day transit time. Only list countries
// that claim actually holds for -- a wrong shipping promise in an Offer is a Merchant Center
// mismatch, not a harmless approximation.
export const SHIPPING_DESTINATION_COUNTRIES = ['FR', 'BE', 'DE', 'NL', 'GB', 'CH', 'LU', 'DK'];

// Profiles that resolve to the same brand elsewhere on the web. `sameAs` is the main lever for
// entity disambiguation in LLMs: only list profiles we control and that are live.
const SAME_AS = [
	'https://www.facebook.com/directos.eu/',
	'https://www.instagram.com/directos.eu/',
	'https://www.youtube.com/@directos',
];

// Topics the shop is an authority on, expressed as Wikipedia URLs rather than free text: an URL
// resolves to a Wikidata entity and stays valid across all six locales, unlike a translated string.
const KNOWS_ABOUT = [
	'https://fr.wikipedia.org/wiki/Charcuterie',
	'https://fr.wikipedia.org/wiki/Salaison',
	'https://fr.wikipedia.org/wiki/Chorizo',
	'https://fr.wikipedia.org/wiki/Fuet',
	'https://fr.wikipedia.org/wiki/Saucisson',
	'https://fr.wikipedia.org/wiki/Jambon_serrano',
];

// The site-wide rating is emitted on every page, so the individual reviews backing it are capped:
// the full validated list would add a few dozen KB to every SSR response for no extra signal.
const MAX_EMBEDDED_REVIEWS = 5;

// `OnlineStore` is the Organization subtype Google expects from a merchant with no physical
// storefront. It keeps every Organization property while typing the entity as a shop, which is
// what disambiguates "Directos" from an unrelated company of the same name.
export function getOrganizationSchema({ description = '', rate = 0, reviewsCount = 0, reviews = [] } = {}) {
	const scoredReviews = (reviews || []).filter(review => review.score).slice(0, MAX_EMBEDDED_REVIEWS);

	return {
		'@type': 'OnlineStore',
		'@id': ORGANIZATION_ID,
		name: config.application.name,
		legalName: 'OFF AXIS',
		// Locale-free, like the @id: one company, one identifier, one url, whatever page is served.
		// The language is carried by WebSite.inLanguage and by the hreflang alternates.
		url: config.url,
		logo: {
			'@type': 'ImageObject',
			url: `${config.url}assets/images/logo.png`,
		},
		image: `${config.url}assets/images/logo.png`,
		...(description ? { description } : {}),
		email: config.application.email,
		// Normalised to E.164: the dotted form used for display is not a valid schema.org telephone.
		telephone: `+${config.application.phone.replace(/\D/g, '')}`,
		vatID: 'FR11814808374',
		address: {
			'@type': 'PostalAddress',
			streetAddress: '15 avenue de la goélette',
			postalCode: '66420',
			addressLocality: 'Le Barcarès',
			addressCountry: 'FR',
		},
		areaServed: AREA_SERVED_COUNTRIES.map(countryCode => ({ '@type': 'Country', name: countryCode })),
		knowsAbout: KNOWS_ABOUT,
		sameAs: SAME_AS,
		// Reviews are about the shop itself, so Google will not turn them into a rich result. They
		// stay in the graph because LLMs do read them when summarising the merchant.
		...(rate && reviewsCount ? {
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: `${rate}`,
				reviewCount: `${reviewsCount}`,
				bestRating: 5,
				worstRating: 1,
			},
		} : {}),
		...(scoredReviews.length ? {
			review: scoredReviews.map(review => ({
				'@type': 'Review',
				...(review.title ? { name: review.title } : {}),
				reviewBody: review.text,
				...((review.dates || {}).created ? { datePublished: dateFormat(review.dates.created, 'YYYY-MM-DD') } : {}),
				author: {
					'@type': 'Person',
					name: review.name,
				},
				reviewRating: {
					'@type': 'Rating',
					ratingValue: review.score,
					bestRating: 5,
					worstRating: 1,
				},
			})),
		} : {}),
	};
}

// References to the site-wide entities, for use from another <script type="application/ld+json">
// block. Google documents neither the merging of separate JSON-LD blocks nor the resolution of an
// @id across them, so a bare { '@id': … } risks staying a dangling pointer. These stubs carry the
// @id (so the nodes collapse into one entity if the parser does resolve it) plus just enough to
// stand alone if it does not. The heavy fields -- sameAs, knowsAbout, address, rating -- are never
// repeated, and @type matches the full node so the merge stays idempotent.
export function getOrganizationRef() {
	return {
		'@type': 'OnlineStore',
		'@id': ORGANIZATION_ID,
		name: config.application.name,
		url: config.url,
	};
}

export function getWebSiteRef() {
	return {
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		name: config.application.name,
		url: config.url,
	};
}

// No `potentialAction`/SearchAction: the app exposes no internal search route, and declaring one
// that 404s is worse than declaring none.
export function getWebSiteSchema({ locale, description = '' } = {}) {
	return {
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		name: config.application.name,
		url: config.url,
		inLanguage: locale,
		...(description ? { description } : {}),
		publisher: { '@id': ORGANIZATION_ID },
	};
}

// Site-wide @graph, emitted once per page by SeoHead.
export function getSiteGraphSchema({ locale, description = '', rate = 0, reviewsCount = 0, reviews = [] } = {}) {
	return {
		'@context': 'https://schema.org',
		'@graph': [
			getOrganizationSchema({ description, rate, reviewsCount, reviews }),
			getWebSiteSchema({ locale, description }),
		],
	};
}
